import React, { useState, useEffect } from 'react'
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material'
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  PictureAsPdf as PdfIcon,
  CheckCircle as ProcessIcon,
  Receipt as CreditNoteIcon,
  MoreVert as MoreVertIcon,
  Print as PrintIcon,
  Email as EmailIcon,
} from '@mui/icons-material'
import { useNavigate, useParams } from 'react-router-dom'
import { salesReturnService } from '../../../services/salesReturnService'
import {
  SalesReturn,
  SalesReturnStatus,
  SalesReturnProcessRequest,
  SALES_RETURN_STATUS_COLORS,
  SALES_RETURN_STATUS_LABELS,
  SALES_RETURN_REASON_COLORS,
  SALES_RETURN_REASON_LABELS
} from '../../../types/salesReturn'
import DocumentAuditHistory from '../../../components/audit/DocumentAuditHistory'

const SalesReturnDetail: React.FC = () => {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  
  const [salesReturn, setSalesReturn] = useState<any | null>(null)
  const [loading, setLoading] = useState(true)
  const [processDialogOpen, setProcessDialogOpen] = useState(false)
  const [processData, setProcessData] = useState({
    refund_amount: 0,
    credit_note_number: '',
    notes: ''
  })
  const [processing, setProcessing] = useState(false)
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

  useEffect(() => {
    if (id) {
      fetchSalesReturn(id)
    }
  }, [id])

  const fetchSalesReturn = async (returnId: string) => {
    try {
      setLoading(true)
      const data = await salesReturnService.getById(returnId)
      setSalesReturn(data)
      setProcessData(prev => ({
        ...prev,
        refund_amount: data.total_amount
      }))
    } catch (error) {
      console.error('Error fetching sales return:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
  }

  const handleDownloadPDF = async () => {
    if (!salesReturn) return
    
    try {
      const blob = await salesReturnService.downloadPDF(salesReturn.id)
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `${salesReturn.return_number}.pdf`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Error downloading PDF:', error)
    }
    handleMenuClose()
  }

  const handleDownloadCreditNote = async () => {
    if (!salesReturn || !salesReturn.credit_note_number) return
    
    try {
      const response = await salesReturnService.generateCreditNote(salesReturn.id)
      // Note: generateCreditNote returns CreditNoteResponse, not a Blob
      console.log('Credit note generated:', response)
    } catch (error) {
      console.error('Error downloading credit note:', error)
    }
    handleMenuClose()
  }

  const handleProcessReturn = async () => {
    if (!salesReturn) return

    try {
      setProcessing(true)
      const processRequest: SalesReturnProcessRequest = {
        refund_amount: processData.refund_amount,
        credit_note_number: processData.credit_note_number || undefined,
        notes: processData.notes || undefined
      }

      await salesReturnService.processReturn(salesReturn.id)
      
      // Refresh the data
      await fetchSalesReturn(salesReturn.id)
      setProcessDialogOpen(false)
    } catch (error) {
      console.error('Error processing return:', error)
    } finally {
      setProcessing(false)
    }
  }

  if (loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <Typography>Loading...</Typography>
      </Box>
    )
  }

  if (!salesReturn) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Sales return not found</Alert>
      </Box>
    )
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton onClick={() => navigate('/admin/documents/sales-returns')} sx={{ mr: 2 }}>
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="h4" fontWeight={600}>
              {salesReturn.return_number}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Sales Return Details
            </Typography>
          </Box>
        </Box>
        
        <Box sx={{ display: 'flex', gap: 1 }}>
          {salesReturn.status === SalesReturnStatus.DRAFT && (
            <Button
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={() => navigate(`/admin/documents/sales-returns/${salesReturn.id}/edit`)}
              sx={{ textTransform: 'none', borderRadius: 2 }}
            >
              Edit
            </Button>
          )}
          
          {salesReturn.status === SalesReturnStatus.APPROVED && (
            <Button
              variant="contained"
              color="success"
              startIcon={<ProcessIcon />}
              onClick={() => setProcessDialogOpen(true)}
              sx={{ textTransform: 'none', borderRadius: 2 }}
            >
              Process Return
            </Button>
          )}

          <IconButton onClick={handleMenuClick}>
            <MoreVertIcon />
          </IconButton>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Main Content */}
        <Grid item xs={12} md={8}>
          {/* Return Information */}
          <Paper sx={{ p: 3, borderRadius: 2, mb: 3 }}>
            <Typography variant="h6" fontWeight={600} mb={3}>
              Return Information
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Return Number
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {salesReturn.return_number}
                </Typography>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Return Date
                </Typography>
                <Typography variant="body1">
                  {new Date(salesReturn.return_date).toLocaleDateString('en-GB')}
                </Typography>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Status
                </Typography>
                <Chip
                  label={SALES_RETURN_STATUS_LABELS[salesReturn.status as SalesReturnStatus] || salesReturn.status}
                  color={SALES_RETURN_STATUS_COLORS[salesReturn.status as SalesReturnStatus] || 'default'}
                  sx={{ borderRadius: 4 }}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Return Reason
                </Typography>
                <Chip
                  label={SALES_RETURN_REASON_LABELS[salesReturn.return_reason as keyof typeof SALES_RETURN_REASON_LABELS] || salesReturn.return_reason}
                  color={SALES_RETURN_REASON_COLORS[salesReturn.return_reason as keyof typeof SALES_RETURN_REASON_COLORS] || 'default'}
                  sx={{ borderRadius: 4 }}
                />
              </Grid>
              
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Return Description
                </Typography>
                <Typography variant="body1">
                  {salesReturn.return_description}
                </Typography>
              </Grid>
            </Grid>
          </Paper>

          {/* Customer & Invoice Information */}
          <Paper sx={{ p: 3, borderRadius: 2, mb: 3 }}>
            <Typography variant="h6" fontWeight={600} mb={3}>
              Customer & Invoice Details
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card sx={{ bgcolor: 'grey.50' }}>
                  <CardContent>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Customer
                    </Typography>
                    <Typography variant="body1" fontWeight={600}>
                      {salesReturn.customer?.business_name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {salesReturn.customer?.contact_person}
                    </Typography>
                    {salesReturn.customer?.email && (
                      <Typography variant="body2" color="text.secondary">
                        {salesReturn.customer.email}
                      </Typography>
                    )}
                    {salesReturn.customer?.phone && (
                      <Typography variant="body2" color="text.secondary">
                        {salesReturn.customer.phone}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Card sx={{ bgcolor: 'grey.50' }}>
                  <CardContent>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Original Invoice
                    </Typography>
                    <Typography variant="body1" fontWeight={600}>
                      {salesReturn.invoice?.invoice_number}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Date: {salesReturn.invoice?.invoice_date ? new Date(salesReturn.invoice.invoice_date).toLocaleDateString('en-GB') : 'N/A'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Amount: ₹{salesReturn.invoice?.total_amount?.toLocaleString('en-IN')}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Paper>

          {/* Return Items */}
          <Paper sx={{ p: 3, borderRadius: 2 }}>
            <Typography variant="h6" fontWeight={600} mb={3}>
              Return Items
            </Typography>
            
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Product</TableCell>
                    <TableCell>SKU</TableCell>
                    <TableCell align="right">Quantity</TableCell>
                    <TableCell align="right">Unit Price</TableCell>
                    <TableCell align="right">Tax Rate</TableCell>
                    <TableCell align="right">Line Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {salesReturn.items.map((item: any, index: number) => (
                    <TableRow key={index}>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {item.variant?.name || 'N/A'}
                        </Typography>
                        {item.description && (
                          <Typography variant="caption" color="text.secondary">
                            {item.description}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {item.variant?.sku || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        {item.quantity}
                      </TableCell>
                      <TableCell align="right">
                        ₹{item.unit_price.toLocaleString('en-IN')}
                      </TableCell>
                      <TableCell align="right">
                        {item.tax_rate || 0}%
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={600}>
                          ₹{(item.line_total || 0).toLocaleString('en-IN')}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        {/* Sidebar */}
        <Grid item xs={12} md={4}>
          {/* Financial Summary */}
          <Paper sx={{ p: 3, borderRadius: 2, mb: 3 }}>
            <Typography variant="h6" fontWeight={600} mb={3}>
              Financial Summary
            </Typography>
            
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">Subtotal:</Typography>
                <Typography variant="body2" fontWeight={600}>
                  ₹{salesReturn.subtotal.toLocaleString('en-IN')}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">Tax Amount:</Typography>
                <Typography variant="body2" fontWeight={600}>
                  ₹{salesReturn.tax_amount.toLocaleString('en-IN')}
                </Typography>
              </Box>
              <Divider sx={{ my: 1 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6" fontWeight={600}>Total Return:</Typography>
                <Typography variant="h6" fontWeight={600} color="error.main">
                  ₹{salesReturn.total_amount.toLocaleString('en-IN')}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body1" fontWeight={600}>Refund Amount:</Typography>
                <Typography variant="body1" fontWeight={600} color="success.main">
                  ₹{salesReturn.refund_amount.toLocaleString('en-IN')}
                </Typography>
              </Box>
            </Box>
          </Paper>

          {/* Credit Note Information */}
          {salesReturn.credit_note_number && (
            <Paper sx={{ p: 3, borderRadius: 2, mb: 3 }}>
              <Typography variant="h6" fontWeight={600} mb={2}>
                Credit Note
              </Typography>
              <Card sx={{ bgcolor: 'success.50' }}>
                <CardContent>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Credit Note Number
                  </Typography>
                  <Typography variant="body1" fontWeight={600} color="success.main">
                    {salesReturn.credit_note_number}
                  </Typography>
                  <Button
                    size="small"
                    startIcon={<CreditNoteIcon />}
                    onClick={handleDownloadCreditNote}
                    sx={{ mt: 1, textTransform: 'none' }}
                  >
                    Download Credit Note
                  </Button>
                </CardContent>
              </Card>
            </Paper>
          )}

          {/* Audit Information */}
          <Paper sx={{ p: 3, borderRadius: 2 }}>
            <Typography variant="h6" fontWeight={600} mb={2}>
              Audit Information
            </Typography>
            
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Created By
              </Typography>
              <Typography variant="body2">
                {salesReturn.created_by_admin?.name || 'N/A'}
              </Typography>
            </Box>
            
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Created At
              </Typography>
              <Typography variant="body2">
                {new Date(salesReturn.created_at).toLocaleString('en-GB')}
              </Typography>
            </Box>
            
            {salesReturn.updated_at && (
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Last Updated
                </Typography>
                <Typography variant="body2">
                  {new Date(salesReturn.updated_at).toLocaleString('en-GB')}
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Audit History */}
        <Grid item xs={12}>
          <DocumentAuditHistory
            documentId={salesReturn.id}
            documentType="sales_return"
            title="Sales Return Audit History"
          />
        </Grid>
      </Grid>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        slotProps={{
          paper: {
            sx: { borderRadius: 2 }
          }
        }}
      >
        <MenuItem onClick={handleDownloadPDF}>
          <ListItemIcon><PdfIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Download PDF</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleMenuClose}>
          <ListItemIcon><PrintIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Print</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleMenuClose}>
          <ListItemIcon><EmailIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Email to Customer</ListItemText>
        </MenuItem>
        {salesReturn.credit_note_number && (
          <MenuItem onClick={handleDownloadCreditNote}>
            <ListItemIcon><CreditNoteIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Download Credit Note</ListItemText>
          </MenuItem>
        )}
      </Menu>

      {/* Process Return Dialog */}
      <Dialog
        open={processDialogOpen}
        onClose={() => setProcessDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Process Sales Return</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Refund Amount"
              type="number"
              value={processData.refund_amount}
              onChange={(e) => setProcessData(prev => ({ ...prev, refund_amount: parseFloat(e.target.value) || 0 }))}
              sx={{ mb: 2 }}
              InputProps={{
                startAdornment: <Typography sx={{ mr: 1 }}>₹</Typography>
              }}
            />
            
            <TextField
              fullWidth
              label="Credit Note Number (Optional)"
              value={processData.credit_note_number}
              onChange={(e) => setProcessData(prev => ({ ...prev, credit_note_number: e.target.value }))}
              sx={{ mb: 2 }}
            />
            
            <TextField
              fullWidth
              label="Processing Notes (Optional)"
              multiline
              rows={3}
              value={processData.notes}
              onChange={(e) => setProcessData(prev => ({ ...prev, notes: e.target.value }))}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProcessDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleProcessReturn}
            disabled={processing}
          >
            {processing ? 'Processing...' : 'Process Return'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default SalesReturnDetail